import React, { useEffect, useState, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetDealerFilterOptionsQuery } from '../../../../api/endpoints/vehicleSale';
import { LISTING_STATUS } from '../utils/statusBadges';

const initialFilters = {
    chassisNo: '',
    brand: '',
    model: '',
    sellerBranchCode: '',
    status: LISTING_STATUS.ACTIVE
};

const SaleListingsFilter = ({ filters, onApplyFilters, onReset, showDealerFilter = true }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const [filterOpen, setFilterOpen] = useState(false);
    const [localFilters, setLocalFilters] = useState({ ...initialFilters, ...filters });

    const { data: dealerOptions = [] } = useGetDealerFilterOptionsQuery({ languageId }, {
        skip: !showDealerFilter
    });

    useEffect(() => {
        setLocalFilters({ ...initialFilters, ...filters });
    }, [filters]);

    const toggleOpen = useCallback(() => setFilterOpen((prev) => !prev), []);

    const handleChange = (field, value) => {
        setLocalFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleApply = () => {
        onApplyFilters(localFilters);
    };

    const handleReset = () => {
        onReset();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleApply();
    };

    const statusOptions = [
        { label: th('vehiclesForSale.listingStatus.active'), value: LISTING_STATUS.ACTIVE },
        { label: th('vehiclesForSale.listingStatus.completed'), value: LISTING_STATUS.COMPLETED },
        { label: th('vehiclesForSale.listingStatus.cancelled'), value: LISTING_STATUS.CANCELLED }
    ];

    const dealerDropdownOptions = dealerOptions.map((d) => ({
        label: d.branchName,
        value: d.branchCode
    }));

    return (
        <div className="generic-table-filter">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button
                        icon="pi pi-filter"
                        label={th('filter.advancedFilter')}
                        onClick={toggleOpen}
                        outlined={!filterOpen}
                    />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">
                        {th('filter.advancedFilters')}
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehiclesForSale.labels.chassisFilter')}
                                </label>
                                <InputText
                                    value={localFilters.chassisNo}
                                    onChange={(e) => handleChange('chassisNo', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehiclesForSale.placeholders.chassisNo')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehiclesForSale.labels.brandFilter')}
                                </label>
                                <InputText
                                    value={localFilters.brand}
                                    onChange={(e) => handleChange('brand', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehiclesForSale.placeholders.brand')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehiclesForSale.labels.modelFilter')}
                                </label>
                                <InputText
                                    value={localFilters.model}
                                    onChange={(e) => handleChange('model', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehiclesForSale.placeholders.model')}
                                />
                            </div>
                        </div>

                        {showDealerFilter && (
                            <div className="col-12 md:col-6 lg:col-3">
                                <div className="field">
                                    <label className="block mb-2 text-sm">
                                        {th('vehiclesForSale.labels.dealerFilter')}
                                    </label>
                                    <Dropdown
                                        value={localFilters.sellerBranchCode}
                                        options={dealerDropdownOptions}
                                        onChange={(e) => handleChange('sellerBranchCode', e.value || '')}
                                        className="w-full"
                                        placeholder={th('vehiclesForSale.placeholders.dealer')}
                                        filter
                                        showClear
                                    />
                                </div>
                            </div>
                        )}

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehiclesForSale.labels.statusFilter')}
                                </label>
                                <Dropdown
                                    value={localFilters.status}
                                    options={statusOptions}
                                    onChange={(e) => handleChange('status', e.value)}
                                    className="w-full"
                                    placeholder={th('vehiclesForSale.placeholders.status')}
                                    showClear
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3 mb-3 gap-2">
                        <Button
                            label={th('filter.reset')}
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={handleReset}
                        />
                        <Button
                            label={th('filter.apply')}
                            icon="pi pi-search"
                            onClick={handleApply}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SaleListingsFilter;
